<template>
  <div id="alt-login"
       class="form-row form-links form-buttons institution-section">
    <div id="siaw-convenience-buttons">
      <div v-if="shouldShowDefaultConvenienceButtons"
           id="default-convenience-buttons">
        <a class="siaw-signin-anchor" 
           href="/login/microsoft" 
           @click.prevent="redirectToSso('/login/microsoft')">
          <UtdButton class="siaw-convenience-button"
                     button-style="ghost-link"
                     tabindex="-1">
            <div class="ghost-link-container">
              <span class="ghost-link-text">
                {{ isMerging ? 'Find' : 'Sign in' }}
                with Microsoft
              </span>
            </div>
            <i class="wk-icon ghost-link-icon microsoft-logo" />
          </UtdButton>
        </a>
        <a class="siaw-signin-anchor" 
           href="/login/open-athens"
           @click.prevent="redirectToSso('/login/open-athens')">
          <UtdButton class="siaw-convenience-button"
                     button-style="ghost-link"
                     tabindex="-1">
            <div class="ghost-link-container">
              <span class="ghost-link-text">
                {{ isMerging ? 'Find' : 'Sign in' }}
                with Open Athens
              </span>
            </div>
            <i class="wk-icon ghost-link-icon open-athens-logo" />
          </UtdButton>
        </a>
      </div>
      <div v-if="!shouldShowDefaultConvenienceButtons"
           id="recent-convenience-button">
        <a class="siaw-signin-anchor" 
           :href="ssoInstitutionSelected.dangerous_url"
           @click.prevent="redirectToSso(ssoInstitutionSelected.dangerous_url)">
          <UtdButton class="siaw-convenience-button"
                     :class="{isPriorityLogin}"
                     button-style="ghost-link"
                     tabindex="-1">
            <div class="ghost-link-container">
              <span class="ghost-link-text">
                {{ isMerging ? 'Find' : 'Sign in' }}
                with {{ ssoInstitutionSelected.dangerous_name }}
              </span>
            </div>
            <i class="wk-icon ghost-link-icon"
               :class="getIcon(ssoInstitutionSelected.dangerous_name)" />
          </UtdButton>
        </a>
      </div>
      <div v-if="!isPriorityLogin">
        <a class="siaw-signin-anchor" 
           href="#"
           @click.prevent="signInAnotherWay()">
          <UtdButton id="signInAnotherWayBtn"
                     class="siaw-convenience-button sign-in-another-way-button" 
                     button-style="ghost-link"
                     tabindex="-1">
            <div class="ghost-link-container">
              <span class="ghost-link-text sign-in-another-way-text">
                {{ isMerging ? $t('LOGIN.FIND_ANOTHER_WAY') 
                  : $t('LOGIN.SIGN_IN_ANOTHER_WAY') }}
              </span>
            </div>
            <i class="wk-icon ghost-link-icon"
               :class="{ 'wkce-icon-chevron-down': !ssoExpanded,
                         'wkce-icon-chevron-up': ssoExpanded }" />
          </UtdButton>
        </a>
      </div>
    </div>
    <div v-if="ssoExpanded" class="sso-search-input">
      <span class="sso-search-helper">
        {{ isMerging ? $t('LOGIN.SSO_MERGE_SEARCH_HELPER') : $t('LOGIN.SSO_SEARCH_HELPER') }}
      </span>
      <UtdSearchInput ref="searchInput" 
                      :options="searchOptions"
                      @keydown.stop="onKeyDown($event)"
                      @utd-search-input="onSsoSearchInput" />
      <AutosuggestDropdownList v-show="showAutoComplete"
                               ref="siAcContainer"
                               :active-index="selectedAcIndex"
                               :list="autoCompleteList"
                               :options="autoCompleteOptions"
                               @keyup.escape="dismissDropdown" />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, nextTick, getCurrentInstance } from 'vue';
import UtdButton from '_acaSrc/components/shared/stdlib/UtdButton.vue';
import UtdSearchInput from '_acaSrc/components/shared/input/UtdSearchInput.vue';
import AutosuggestDropdownList from 
    '_acaSrc/components/contents/search/autocomplete/AutocompleteDropdownList.vue';
import { debounce } from '_acaSrc/utility/timers';
import {
    C_AUTOCOMPLETE,
    C_KEYCODES,
    C_UI,
    C_PRIORITY_LOGIN,
    maxSearchInputLength,
    SSO_INSTITUTION_SELECTION
} from '_acaSrc/utility/constants';
import { getDecodeCookie } from '_acaSrc/utility/Cookies';
import ACDropdownValue from
    '_acaSrc/components/contents/search/autocomplete/AutocompleteDropdownValue';
import utdRest from '_acaSrc/utility/http/UtdRestHooks';
import { incrementCarousel } from '_acaSrc/utility/math';
import Logger from '_acaSrc/utility/Logger';
import UtdCache from '_acaSrc/utility/UtdCache';

const MIN_SEARCH_LENGTH = 1;
const { DOWN_ARROW, UP_ARROW, ESCAPE_KEYCODE, ENTER_KEY } = C_KEYCODES;

const { proxy } = getCurrentInstance();
const translate = (key) => proxy.$t(key);

const { isMerging } = defineProps({
    isMerging: {
        type: Boolean,
        default: false
    },
    isPriorityLogin: { // utd3-767 - changes visual style and hides "sign in another way"
        type: Boolean,
        default: false
    }
});

const emit = defineEmits([ 'show-spinner', 'fetched-autocomplete' ]);

const searchInput = ref(null);
const siAcContainer = ref(null);
const availableInstitutions = ref([]);
const autoCompleteList = ref([]);
const isUnexpectedError = ref(false);
const isAutoCompleteVisible = ref(false);
const ssoExpanded = ref(false);
const searchEntry = ref('');
const selectedAcIndex = ref(-1);
const isLoading = ref(false);
const debounceSearchFn = ref(null);

const showAutoComplete = computed(() => 
    isAutoCompleteVisible.value && searchEntry.value.length > MIN_SEARCH_LENGTH
);
        
const searchOptions = computed(() => ({
    placeholder: isMerging 
        ? translate('LOGIN.SSO_MERGE_AUTOCOMPLETE_PLACEHOLDER') 
        : translate('LOGIN.SSO_LOGIN_AUTOCOMPLETE_PLACEHOLDER'),
    showSpinner: isLoading.value,
    inputAttrs: {
        maxlength: maxSearchInputLength,
        autocomplete: 'off'
    }
}));
        
const autoCompleteOptions = computed(() => ({
    autoCompleteTerm: searchEntry.value
}));
        
const ssoInstitutionSelected = computed(() => {
    const ssoInstitution = getDecodeCookie(SSO_INSTITUTION_SELECTION);
    try {
        return JSON.parse(ssoInstitution);
    }
    catch {
        return null;
    }
});

const onKeyDown = (e) => {
    try {
        if (e.keyCode === DOWN_ARROW || e.keyCode === UP_ARROW) {
            handleACKeyboardNav(e);
        }
        else if (e.keyCode === ESCAPE_KEYCODE) {
            isAutoCompleteVisible.value = false;
        }
        else if (e.keyCode === ENTER_KEY) {
            e.preventDefault();
            navigateToSsoLogin();
        }
    }
    catch (error) {
        Logger.error(`Error processing key input with key=${e.keyCode} error:`, error);
    }
};

const handleACKeyboardNav = (e) => {
    const prevIdx = selectedAcIndex.value;

    if (availableInstitutions.value.length === 0) {
        return;
    }

    if (prevIdx < 0) {
        selectedAcIndex.value = e.keyCode === UP_ARROW
            ? availableInstitutions.value.length - 1
            : 0;
    }
    else {
        const incr = e.keyCode === UP_ARROW ? -1 : 1;
        selectedAcIndex.value = incrementCarousel(
            prevIdx, incr, availableInstitutions.value.length
        );
    }
    if (e.keyCode === UP_ARROW) {
        e.preventDefault();
    }
};

const navigateToSsoLogin = () => {
    if (availableInstitutions.value.length === 0) {
        return;
    }
    if (selectedAcIndex.value < 0) {
        return;
    }
    const selectedSsoInst = availableInstitutions.value[selectedAcIndex.value];
    if (selectedSsoInst.url) {
        redirectToSso(selectedSsoInst.url);
    }
};

const onSsoSearchInput = (value) => {
    searchEntry.value = value.toLowerCase();
    selectedAcIndex.value = -1;
    if (!value || !(value.length > MIN_SEARCH_LENGTH)) {
        isAutoCompleteVisible.value = false;
        return;
    }
    isAutoCompleteVisible.value = true;
    isLoading.value = true;
    debounceSearchFn.value(value);
};

const makeSsoSearchHeader = (label) => 
    (new ACDropdownValue())
        .setType(C_AUTOCOMPLETE.ITEM_TYPES.HEADER.DEFAULT)
        .setLabel(label)
        .setClasses('ac-header')
        .setElId('ac-search-suggestions-header');

const makeSsoSearchLink = (r, i) => 
    (new ACDropdownValue())
        .setType(C_AUTOCOMPLETE.ITEM_TYPES.CLICKABLE.DEFAULT)
        .setElId(`search-suggestion-${i}`)
        .setUniqueId(`search-suggestion-${r.name}-${i}`)
        .setClasses('ac-search-suggestion')
        .setOnClick(() => redirectToSso(r.url))
        .setSubClasses('wkce-icon-search')
        .setLabel(r.name);

const makeNoResultsFound = (label) => 
    (new ACDropdownValue())
        .setType('header')
        .setClasses('ac-italic ac-generic')
        .setLabel(label);

const fetchSsoInstitutions = async() => {
    if (searchEntry.value <= MIN_SEARCH_LENGTH) {
        return;
    }
    try {
        const data = await utdRest('wayf/institutions/siaw', {
            searchTerm: searchEntry.value
        });
        availableInstitutions.value = data.data || data;
    }
    catch {
        isUnexpectedError.value = true;
        availableInstitutions.value = [];
    }
};

const getAutocompleteList = async() => {
    const returnList = [];
    await fetchSsoInstitutions();
    if (availableInstitutions.value.length > 0) {
        returnList.push(makeSsoSearchHeader(
            translate('SEARCH.SEARCH_SUGGESTIONS').toUpperCase()
        ));
        availableInstitutions.value.forEach((r, i) => {
            if (r.name && r.url) {
                returnList.push(makeSsoSearchLink(r, i));
            }
        });
    }
    else {
        returnList.push(makeNoResultsFound(
            translate('SEARCH.NO_RESULTS_FOUND')
        ));
    }
    autoCompleteList.value = returnList;
    isLoading.value = false;
    emit('fetched-autocomplete');
};

const signInAnotherWay = () => {
    ssoExpanded.value = !ssoExpanded.value;
    if (ssoExpanded.value) {
        nextTick(() => {
            searchInput.value.autoFocus(); 
        }); 
    }
};

const redirectToSso = (ssoUrl) => {
    if (!isMerging) {
        new UtdCache().setPersistent(
            C_PRIORITY_LOGIN.LOGIN_PRIORITY_CACHE_KEY,
            C_PRIORITY_LOGIN.SSO_PRIORITY, false);
    }

    emit('show-spinner', ssoUrl);
};

const getIcon = (institutionName) => {
    if (institutionName === 'Microsoft') {
        return 'microsoft-logo';
    }
    else if (institutionName === 'Open Athens') {
        return 'open-athens-logo';
    }
    return 'wkce-icon-link-external';
};

const dismissDropdown = () => {
    isAutoCompleteVisible.value = false;
    searchInput.value.autoFocus();
};

const shouldShowDefaultConvenienceButtons = computed(() => 
    ssoInstitutionSelected.value === null || isMerging
);

// Lifecycle
onMounted(() => {
    debounceSearchFn.value = debounce(
        getAutocompleteList, C_UI.AUTOCOMPLETE_DROPDOWN_DELAY
    );
});
</script>

<style lang="less" scoped>
@import '~_acaAssets/global/variables';
@import '~_acaAssets/wkce/colors/wkce-app-styles';
@SELECTBOX-FONT-COLOR: @WKCE-BLUE-SHADE1;

#siaw-convenience-buttons .siaw-signin-anchor:hover {
  text-decoration: none;
}

.institution-section {
  .sso-search-input {
    .ds1-mt-4();
    .ds1-flex-column-wrap();

    :deep(.autocomplete) {
      position: static;
    }

    :deep(#search-input) {
      .ds1-pr-5();
    }
  }

  .sso-search-helper {
    .ds1-utd-weight-600();
    .ds1-mb-1();
  }

  .wk-button-icon {
    margin-left: 4px;
  }
}

.form-links.institution-section {
  .ds1-mt-3();
  display: flex;
  flex-direction: column;

  .form-row {
    text-align: center;
  }

  #siaw-form {
    padding-top: 20px;
  }

  .form-buttons-wrapper {
    display: flex;
    flex-wrap: wrap;

    > a:nth-child(2) {
      .ds1-mh-3();
    }
  }

  & > * {
    float: none;
  }

  > h3 {
    .ds1-utd-size-3();
  }

  .institution-selector {
    border: none;
    position: relative;
    width: 119px;
    margin-left: -2px;
    margin-top: 14px;
    background-color: transparent;
    color: @SELECTBOX-FONT-COLOR;
  }
}

#siaw-convenience-buttons > :not(:first-child) .siaw-convenience-button {
  .ds1-mt-2();
}

#siaw-convenience-buttons > #default-convenience-buttons >
:not(:first-child) .siaw-convenience-button {
  .ds1-mt-2();
}

.siaw-convenience-button.isPriorityLogin {
  background-color: @WKCE-BLUE;
  color: @WKCE-WHITE;
  border: none;

  &:hover {
    background-color: @WKCE-BLUE-SHADE2;

    .ghost-link-text {
      border-bottom-color: @WKCE-WHITE;
    }
  }

  .ghost-link-icon {
    color: @WKCE-WHITE;
  }
}

.microsoft-logo {
  background: url(~_acaAssets/login/microsoft-logo-blue.svg) no-repeat;
  height: 22.1px;
  width: 22px;
}

.open-athens-logo {
  background: url(~_acaAssets/login/open-athens-logo-purple.svg) no-repeat;
  height: 26px;
  width: 24.88px;
}

#signInAnotherWayBtn {
  .wk-icon {
    padding: 5px;
  }
}

.wkce-icon-link-external::before {
  font-size: 22px;
}

.xAIMobile {
  .siaw-convenience-button {
    background-color: @WKCE-CREAM-TINT1;
    border-radius: 50px;
    text-align: center; 

    .ghost-link-container {
      max-width: none; 
      width: 100%; 
    }

    .ghost-link-text {
      color: @WKCE-BLACK; 
      font-size: 14px;
      font-weight: 400;
    } 
  }

  .wk-icon {
    display: none; 
  }

  .sign-in-another-way-button {
      background-color: @WKCE-CREAM-TINT3;
    
      .sign-in-another-way-text {
        color: @WKCE-CREAM-SHADE1;
      }
    }
 
}

@media only screen and (min-width: 768px) {
  .isDesktop .utd-form .form-links.institution-section {
    .ds1-pa-0();

    > h3 {
      .ds1-utd-h3();
    }

    .form-buttons-wrapper {
      > a:nth-child(2) {
        .ds1-mh-4();
      }
    }
  }
}

</style>

<style lang="less">
.sso-search-input .utd-autocomplete-ul {
  overflow-y: auto;
  max-height: 210px;
}
</style>